import React, { useState, useEffect, useRef } from 'react';
import { Users, Loader2, Briefcase, Building, Star, Palette, Mic, Disc } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { User, UserRole } from '../types';

interface UserSearchProps {
  onSelectUser: (user: User | null) => void;
  selectedUserName?: string;
  className?: string;
}

const roleIcons: { [key in UserRole]: React.ElementType } = {
    ARTIST: Palette,
    VENUE: Building,
    REVELLER: Star,
    SERVICE: Mic,
    ORGANIZER: Briefcase,
    SPONSOR: Disc,
    ADMIN: Briefcase,
    SYSTEM_ADMIN_LIVE: Briefcase,
};

const UserSearch: React.FC<UserSearchProps> = ({ onSelectUser, selectedUserName, className }) => {
  const [searchTerm, setSearchTerm] = useState(selectedUserName || '');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchTerm(selectedUserName || '');
  }, [selectedUserName]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (searchTerm.length < 1) {
        setUsers([]);
        return;
      }
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/users?name=${searchTerm}`);
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error(error);
        setUsers([]);
      }
      setIsLoading(false);
    };

    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== selectedUserName) {
        fetchUsers();
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedUserName]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownVisible(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (user: User) => {
    onSelectUser(user);
    setSearchTerm(user.name);
    setIsDropdownVisible(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '') {
      onSelectUser(null);
    }
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-kala-500" />
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => setIsDropdownVisible(true)}
        className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none"
        placeholder="Search for any user..."
      />
      {isDropdownVisible && (
        <div className="absolute z-20 w-full mt-1 bg-kala-800 border border-kala-700 rounded-lg shadow-lg">
          {isLoading ? (
            <div className="p-4 flex justify-center items-center">
              <Loader2 className="w-5 h-5 text-kala-secondary animate-spin" />
            </div>
          ) : (
            <ul className="max-h-60 overflow-y-auto">
              {users.length > 0 ? (
                users.map((user) => {
                  const Icon = roleIcons[user.role as UserRole] || Users;
                  return (
                    <li
                      key={user.id}
                      onClick={() => handleSelect(user)}
                      className="px-4 py-3 hover:bg-kala-700 cursor-pointer text-white flex items-center gap-3"
                    >
                      <Icon className="w-5 h-5 text-kala-400" />
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-xs text-kala-400">{user.bio || 'No bio available.'}</p>
                      </div>
                    </li>
                  );
                })
              ) : (
                searchTerm.length > 0 && <li className="px-4 py-3 text-kala-400">No users found for '{searchTerm}'.</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearch;
